import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import StarIcon from "@mui/icons-material/Star";
import React, { useState } from "react";
import { useVersionHistory } from "../../../hooks/api/projectDocumentsApiHooks";
import { toast } from "react-toastify";

/**
 * Modal para exibir histórico de versões de um documento
 * @param {object} props
 * @param {boolean} props.isOpen - Se o modal está aberto
 * @param {function} props.onClose - Callback ao fechar
 * @param {number} props.projectId - ID do projeto
 * @param {object} props.documentType - Tipo de documento
 */
export default function DocumentVersionHistoryModal({
  isOpen,
  onClose,
  projectId,
  documentType,
}) {
  const [downloadingId, setDownloadingId] = useState(null);

  // Buscar histórico de versões
  const { data: versions, isLoading, error } = useVersionHistory(
    projectId,
    documentType?.id,
    isOpen
  );

  // Handler de download
  const handleDownload = async (document) => {
    try {
      setDownloadingId(document.id);
      
      // Usar downloadUrl diretamente como o FileManager faz
      if (document.downloadUrl) {
        const anchor = document.createElement("a");
        anchor.href = document.downloadUrl;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        toast.success("Download iniciado!");
      } else {
        toast.error("URL de download não disponível");
      }
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      toast.error("Erro ao fazer download do documento");
    } finally {
      setDownloadingId(null);
    }
  };

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR") + " às " + date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Histórico: {documentType?.name}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="error">
              Erro ao carregar histórico: {error.message}
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && !error && (!versions || versions.length === 0) && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              Nenhuma versão encontrada para este documento
            </Typography>
          </Box>
        )}

        {/* Lista de Versões */}
        {!isLoading && !error && versions && versions.length > 0 && (
          <Box>
            {versions.map((document, index) => {
              const isCurrentVersion = document.isCurrent;
              const isDownloading = downloadingId === document.id;

              return (
                <Box key={document.id}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: isCurrentVersion ? "action.selected" : "transparent",
                      borderRadius: 1,
                      border: isCurrentVersion ? "2px solid" : "none",
                      borderColor: isCurrentVersion ? "primary.main" : "transparent",
                    }}
                  >
                    {/* Header da Versão */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Versão {document.version}
                        </Typography>
                        {isCurrentVersion && (
                          <Chip
                            label="Atual"
                            icon={<StarIcon />}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(document)}
                        disabled={isDownloading}
                      >
                        {isDownloading ? "Baixando..." : "Baixar"}
                      </Button>
                    </Box>

                    {/* Informações do Arquivo */}
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      📄 {document.fileName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatFileSize(document.fileSize)}
                    </Typography>

                    {/* Informações de Upload */}
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Enviado por:</strong> {document.uploadedBy?.name || "Desconhecido"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Data:</strong> {formatDate(document.uploadDate)}
                      </Typography>
                    </Box>

                    {/* Comentários */}
                    {document.comments && (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1,
                          bgcolor: "background.default",
                          borderRadius: 1,
                          borderLeft: "3px solid",
                          borderColor: "primary.main",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Comentários:
                        </Typography>
                        <Typography variant="body2">{document.comments}</Typography>
                      </Box>
                    )}
                  </Box>
                  {index < versions.length - 1 && <Divider sx={{ my: 1.5 }} />}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

